import { Label } from '@/components/ui/label';
import { TextRow, HtmlField, ImageField, Panel, RowTools, AddButton, words } from './EditorFields';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

export const BasicTab = ({ f, set }) => (
  <div className="space-y-5">
    <Panel title="Identity">
      <div className="grid md:grid-cols-2 gap-4">
        <TextRow label="Job Fair Name *" value={f.name} onChange={set('name')} testId="editor-name-input" />
        <TextRow label="URL Name *" value={f.slug} onChange={(v) => set('slug')(v.toLowerCase().replace(/[^a-z0-9-]/g, '-'))} testId="editor-slug-input" hint={`Public page: /jobfairs/${f.slug || ''}`} />
        <TextRow label="Registration ID Prefix" value={f.reg_prefix} onChange={(v) => set('reg_prefix')(v.toUpperCase().replace(/[^A-Z0-9]/g, ''))} maxLength={8} testId="editor-prefix-input" hint={`IDs look like ${f.reg_prefix || 'JF'}-000001 / ${f.reg_prefix || 'JF'}-EMP-000001`} />
      </div>
      <div>
        <Label className="text-sm">Brief Description (about 20 words)</Label>
        <Textarea rows={2} value={f.short_description || ''} onChange={(e) => set('short_description')(e.target.value)} data-testid="editor-short-description-input" />
        <p className={`text-xs mt-1 ${words(f.short_description) > 20 ? 'text-amber-600' : 'text-slate-400'}`}>{words(f.short_description)} / 20 words</p>
      </div>
      <HtmlField label="Detailed Description (HTML, shown below the hero)" value={f.detail_description} onChange={set('detail_description')} rows={8} maxWords={1000} testId="editor-detail-description-input" />
    </Panel>
    <Panel title="Date & Time" desc="After the end date/time passes, the page automatically shows COMPLETED and registrations close.">
      <div className="grid md:grid-cols-3 gap-4">
        <div><Label className="text-sm">Starts</Label><Input type="datetime-local" value={f.start_datetime || ''} onChange={(e) => set('start_datetime')(e.target.value)} data-testid="editor-start-input" /></div>
        <div><Label className="text-sm">Ends</Label><Input type="datetime-local" value={f.end_datetime || ''} onChange={(e) => set('end_datetime')(e.target.value)} data-testid="editor-end-input" /></div>
        <TextRow label="Display Text (optional)" value={f.date_display} onChange={set('date_display')} placeholder="12 & 13 December 2026" testId="editor-date-display-input" />
      </div>
    </Panel>
    <Panel title="Venue & Location">
      <div className="grid md:grid-cols-2 gap-4">
        <TextRow label="Venue Name" value={f.venue_name} onChange={set('venue_name')} testId="editor-venue-input" />
        <TextRow label="City" value={f.city} onChange={set('city')} testId="editor-city-input" />
        <TextRow label="State" value={f.state} onChange={set('state')} testId="editor-state-input" />
        <TextRow label="Google Maps Link (optional)" value={f.map_url} onChange={set('map_url')} testId="editor-map-url-input" />
        <TextRow label="Latitude (optional)" value={f.latitude} onChange={set('latitude')} testId="editor-lat-input" />
        <TextRow label="Longitude (optional)" value={f.longitude} onChange={set('longitude')} testId="editor-lng-input" />
      </div>
      <div><Label className="text-sm">Full Address</Label><Textarea rows={2} value={f.address || ''} onChange={(e) => set('address')(e.target.value)} data-testid="editor-address-input" /></div>
    </Panel>
    <Panel title="Contact (shown in footer)">
      <div className="grid md:grid-cols-2 gap-4">
        <TextRow label="Email" value={f.contact_email} onChange={set('contact_email')} testId="editor-contact-email-input" />
        <TextRow label="Phone" value={f.contact_phone} onChange={set('contact_phone')} testId="editor-contact-phone-input" />
      </div>
    </Panel>
  </div>
);

export const HeroTab = ({ f, set }) => {
  const hl = f.highlights || [];
  const upd = (i, k, v) => set('highlights')(hl.map((h, j) => (j === i ? { ...h, [k]: v } : h)));
  return (
    <div className="space-y-5">
      <Panel title="Branding & Hero">
        <div className="grid md:grid-cols-2 gap-6">
          <ImageField fairId={f.id} label="Job Fair Logo (header & footer)" value={f.logo} onChange={set('logo')} testId="editor-logo" />
          <ImageField fairId={f.id} label="Hero Image" value={f.hero_image} onChange={set('hero_image')} testId="editor-hero-image" />
        </div>
        <TextRow label="Slogan" value={f.slogan} onChange={set('slogan')} testId="editor-slogan-input" />
        <TextRow label="Tagline" value={f.tagline} onChange={set('tagline')} testId="editor-tagline-input" />
      </Panel>
      <Panel title="Highlights" desc="Stat cards under the hero, e.g. “5,000 – 7,000” · “Job Opportunities”">
        {hl.map((h, i) => (
          <div key={i} className="flex gap-2 items-center" data-testid={`editor-highlight-${i}`}>
            <Input value={h.value || ''} onChange={(e) => upd(i, 'value', e.target.value)} placeholder="Value" className="w-48" data-testid={`editor-highlight-value-${i}`} />
            <Input value={h.label || ''} onChange={(e) => upd(i, 'label', e.target.value)} placeholder="Label" data-testid={`editor-highlight-label-${i}`} />
            <RowTools list={hl} i={i} onChange={set('highlights')} testId="editor-highlight" />
          </div>
        ))}
        <AddButton label="Add highlight" onClick={() => set('highlights')([...hl, { value: '', label: '' }])} testId="editor-highlight-add" />
      </Panel>
    </div>
  );
};

const PeopleList = ({ f, list, onChange, withCategory, testId, addLabel }) => {
  const upd = (i, k, v) => onChange(list.map((o, j) => (j === i ? { ...o, [k]: v } : o)));
  return (
    <div className="space-y-3">
      {list.map((o, i) => (
        <div key={i} className="border rounded-lg p-3 grid md:grid-cols-[1fr_1fr] gap-3" data-testid={`${testId}-${i}`}>
          <div className="space-y-2">
            <Input value={o.name || ''} onChange={(e) => upd(i, 'name', e.target.value)} placeholder="Name" data-testid={`${testId}-name-${i}`} />
            <Input value={o.url || ''} onChange={(e) => upd(i, 'url', e.target.value)} placeholder="Website (optional)" />
            {withCategory ? (
              <select className="w-full h-10 rounded-md border px-3 text-sm" value={o.category || 'Partner'} onChange={(e) => upd(i, 'category', e.target.value)} data-testid={`${testId}-category-${i}`}>
                <option>Partner</option><option>Sponsor</option>
              </select>
            ) : <Input value={o.role || ''} onChange={(e) => upd(i, 'role', e.target.value)} placeholder="Role (e.g. Organiser)" />}
          </div>
          <div className="flex gap-2">
            <div className="flex-1"><ImageField fairId={f.id} value={o.logo} onChange={(v) => upd(i, 'logo', v)} testId={`${testId}-logo-${i}`} /></div>
            <RowTools list={list} i={i} onChange={onChange} testId={testId} />
          </div>
        </div>
      ))}
      <AddButton label={addLabel} onClick={() => onChange([...list, withCategory ? { name: '', logo: '', url: '', category: 'Partner' } : { name: '', logo: '', url: '', role: '' }])} testId={`${testId}-add`} />
    </div>
  );
};

export const PartnersTab = ({ f, set }) => (
  <div className="space-y-5">
    <Panel title="Organizers"><PeopleList f={f} list={f.organizers || []} onChange={set('organizers')} testId="editor-organizer" addLabel="Add organizer" /></Panel>
    <Panel title="Partners & Sponsors"><PeopleList f={f} list={f.partners || []} onChange={set('partners')} withCategory testId="editor-partner" addLabel="Add partner / sponsor" /></Panel>
  </div>
);

export const FooterTab = ({ f, set }) => (
  <div className="space-y-5">
    <Panel title="Footer Declaration"><HtmlField label="Footer text (HTML)" value={f.footer_text} onChange={set('footer_text')} rows={4} testId="editor-footer-input" /></Panel>
    <Panel title="Theme Colours" desc="Leave empty to use the default coral & navy theme.">
      <div className="grid md:grid-cols-2 gap-4">
        {[['theme_primary', 'Primary (buttons, accents)', '#E05A47'], ['theme_secondary', 'Secondary (dark sections)', '#1E1B4B']].map(([k, l, d]) => (
          <div key={k}>
            <Label className="text-sm">{l}</Label>
            <div className="flex gap-2 mt-1">
              <input type="color" value={f[k] || d} onChange={(e) => set(k)(e.target.value)} className="h-10 w-14 rounded border" data-testid={`editor-${k}-picker`} />
              <Input value={f[k] || ''} onChange={(e) => set(k)(e.target.value)} placeholder={d} />
            </div>
          </div>
        ))}
      </div>
    </Panel>
  </div>
);
